// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import SlideOverPanel from './SlideOverPanel';

export interface DeleteAnalysisPanelProps {
  isOpen: boolean;
  analysisName: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const PRESET_REASONS = ['Duplicate', 'Completed / no longer needed', 'Wrong deal or company', 'Test or draft', 'Other'];

export default function DeleteAnalysisPanel({ isOpen, analysisName, onClose, onConfirm }: DeleteAnalysisPanelProps) {
  const [preset, setPreset] = useState('');
  const [otherReason, setOtherReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPreset('');
      setOtherReason('');
    }
  }, [isOpen]);

  const reason = preset === 'Other' ? otherReason.trim() : preset;
  const canConfirm = reason.length > 0;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(reason);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  if (!isOpen) return null;

  return (
    <SlideOverPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Move to Archives"
      animationClass="delete-panel"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          >
            <Trash2 size={16} />
            Move to Archives
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        This analysis will be moved to Archives. You can restore it from Archives later.
      </p>
      <div className="py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Analysis</p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">{analysisName || 'Untitled analysis'}</p>
      </div>
      <div onKeyDown={handleKeyDown}>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Reason for archiving</label>
        <div className="space-y-2">
          {PRESET_REASONS.map((r) => (
            <label key={r} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="reason"
                value={r}
                checked={preset === r}
                onChange={() => setPreset(r)}
                className="rounded-full border-gray-300 text-gray-900 focus:ring-gray-500"
              />
              <span className="text-sm text-gray-700">{r}</span>
            </label>
          ))}
        </div>
        {preset === 'Other' && (
          <textarea
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
            placeholder="Please specify..."
            rows={3}
            className="mt-3 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none resize-none"
            autoFocus
          />
        )}
      </div>
    </SlideOverPanel>
  );
}
